/**
 * Read-side rules for key value edits (W3-05; UX-033, UX-042). Pure helpers over a document snapshot that the
 * motion columns, the curve editor, the note lane's paste flow and the inspector share:
 *
 *  - `isJumpKey`, `jumpsMade`: which keys are jumps (arrive != leave, never the first key of a track), and which
 *    keys a transaction turned from continuous into jumps (the "Made a jump at 3:0/1" toast with Undo and
 *    "Continue from previous").
 *  - `valueDomain`, `clampToDomain`: the range a relative adjustment (RPE Ctrl+wheel fine-tune, arrow nudges) keeps
 *    a value in. Opacity is 0..255 (the inspector's "0-255" badge) and GIF progress 0..1. A value that already lies
 *    outside its domain (an imported negative opacity hides the line's notes in Phira, R§3.3) is never pulled in by
 *    a nudge, only kept from moving further out. Typed values are not clamped: they are explicit.
 */

import type { ChartDoc, KeyId, LineProp, TrackValue } from '../model/types';
import { trackValueEquals } from '../model/invariants';
import { Beats } from '../time/beat';

interface KeyLike {
  readonly vIn: TrackValue;
  readonly vOut: TrackValue;
}

/** Index lookups the rules need (DocIndexes has them). */
export interface PrevKeyIndex {
  prevKey(id: KeyId): KeyId | undefined;
}

/** True when the key's arrive and leave values differ and it is not the first key of its track. */
export function isJumpKey(doc: ChartDoc, idx: PrevKeyIndex, id: KeyId): boolean {
  const k = doc.keys.get(id);
  if (k === undefined || idx.prevKey(id) === undefined) return false;
  return !trackValueEquals(k.vIn, k.vOut);
}

/** True when a key record arrives and leaves at the same value. */
export function isContinuous(k: KeyLike): boolean {
  return trackValueEquals(k.vIn, k.vOut);
}

/**
 * Which of `ids` are continuous now (a DocStore's `doc` changes in place with every commit: record what a later
 * `jumpsMade` compares against BEFORE the transaction).
 */
export function continuityOf(doc: ChartDoc, ids: Iterable<KeyId>): Map<KeyId, boolean> {
  const out = new Map<KeyId, boolean>();
  for (const id of ids) {
    const k = doc.keys.get(id);
    if (k !== undefined) out.set(id, isContinuous(k));
  }
  return out;
}

/**
 * Keys among `ids` that were continuous before (`before`, from `continuityOf`) and are jumps in `after` (sorted by
 * beat, then id). Keys unknown before are skipped.
 */
export function jumpsMade(
  before: ReadonlyMap<KeyId, boolean>,
  after: { readonly doc: ChartDoc; readonly idx: PrevKeyIndex },
  ids: Iterable<KeyId>,
): KeyId[] {
  const out: KeyId[] = [];
  for (const id of new Set(ids)) {
    if (before.get(id) !== true) continue;
    if (isJumpKey(after.doc, after.idx, id)) out.push(id);
  }
  return out.sort((a, b) => {
    const ka = after.doc.keys.get(a);
    const kb = after.doc.keys.get(b);
    if (ka === undefined || kb === undefined) return a - b;
    return Beats.cmp(ka.t, kb.t) || a - b;
  });
}

/** The range relative adjustments keep a property's raw value in, or null when it is unbounded. */
export function valueDomain(prop: LineProp): readonly [number, number] | null {
  switch (prop) {
    case 'alpha':
      return [0, 255];
    case 'gif':
      return [0, 1];
    default:
      return null;
  }
}

/**
 * `next` kept inside the property's domain: a value moving out of it stops at the edge; a value that already was
 * outside (imported) may move back towards the domain but never further out.
 */
export function clampToDomain(prop: LineProp, current: number, next: number): number {
  const d = valueDomain(prop);
  if (d === null || !Number.isFinite(next)) return next;
  const [lo, hi] = d;
  if (next > hi) return Math.max(hi, Math.min(next, current));
  if (next < lo) return Math.min(lo, Math.max(next, current));
  return next;
}
